import api from './api';
import type {
  EmailSendingConfig,
  ClientEmailDomain,
  RemetenteConfigResponse,
  SetRemetentePayload,
  SetDominioPayload,
  EmailTemplate,
  CriarTemplatePayload,
  AtualizarTemplatePayload,
  DisparoEmail,
  DisparoEmailContato,
  CampanhaPreview,
  EntregaResumo,
} from '@/types/email';

export const emailService = {
  async getDominio(): Promise<EmailSendingConfig | null> {
    // api.get devolve o envelope cru { success, data }. data pode ser null (sem
    // domínio configurado), então desempacotamos só .data — sem fallback para o
    // envelope, que seria truthy e quebraria o estado "sem domínio" na UI.
    const result = (await api.get('/email/dominio')) as any;
    return result?.data ?? null;
  },

  async setDominio(payload: SetDominioPayload): Promise<EmailSendingConfig> {
    const result = (await api.put('/email/dominio', payload)) as any;
    return result?.data ?? result;
  },

  async verificarDominio(): Promise<EmailSendingConfig> {
    const result = (await api.post('/email/dominio/verificar', {})) as any;
    return result?.data ?? result;
  },

  async removerDominio(): Promise<void> {
    await api.delete('/email/dominio');
  },

  async getClientDominio(clientId: number): Promise<ClientEmailDomain | null> {
    const result = (await api.get(`/email/client/${clientId}/dominio`)) as any;
    return result?.data ?? null;
  },

  async setClientDominio(clientId: number, payload: SetDominioPayload): Promise<ClientEmailDomain> {
    const result = (await api.put(`/email/client/${clientId}/dominio`, payload)) as any;
    return result?.data ?? result;
  },

  async verificarClientDominio(clientId: number): Promise<ClientEmailDomain> {
    const result = (await api.post(`/email/client/${clientId}/dominio/verificar`, {})) as any;
    return result?.data ?? result;
  },

  async removerClientDominio(clientId: number): Promise<void> {
    await api.delete(`/email/client/${clientId}/dominio`);
  },

  async getRemetente(clientId: number): Promise<RemetenteConfigResponse | null> {
    const result = (await api.get(`/email/remetente/${clientId}`)) as any;
    return result?.data ?? null;
  },

  async setRemetente(clientId: number, payload: SetRemetentePayload): Promise<void> {
    await api.put(`/email/remetente/${clientId}`, payload);
  },

  async listTemplates(clientId?: number): Promise<EmailTemplate[]> {
    const params: Record<string, string> = {};
    if (clientId) params.client_id = String(clientId);
    const result = (await api.get('/email/templates', { params })) as any;
    return result?.data ?? result ?? [];
  },

  async criarTemplate(payload: CriarTemplatePayload): Promise<EmailTemplate> {
    const result = (await api.post('/email/templates', payload)) as any;
    return result?.data ?? result;
  },

  async atualizarTemplate(id: number, payload: AtualizarTemplatePayload): Promise<EmailTemplate> {
    const result = (await api.put(`/email/templates/${id}`, payload)) as any;
    return result?.data ?? result;
  },

  async removerTemplate(id: number): Promise<void> {
    await api.delete(`/email/templates/${id}`);
  },

  async getColunasBoleto(): Promise<string[]> {
    const result = (await api.get('/template-variable-mappings/columns/valid')) as any;
    return result?.data ?? result ?? [];
  },

  async previewCampanha(formData: FormData): Promise<CampanhaPreview> {
    const result = (await api.post('/email/campanhas/preview', formData)) as any;
    return result?.data ?? result;
  },

  async criarCampanha(formData: FormData): Promise<{ disparo: DisparoEmail; created: number; invalidos: number }> {
    const result = (await api.post('/email/campanhas', formData)) as any;
    return {
      disparo: result?.data ?? result,
      created: result?.created ?? 0,
      invalidos: result?.invalidos ?? 0,
    };
  },

  async listCampanhas(clientId?: number): Promise<DisparoEmail[]> {
    const params: Record<string, string> = {};
    if (clientId) params.client_id = String(clientId);
    const result = (await api.get('/email/campanhas', { params })) as any;
    return result?.data ?? result ?? [];
  },

  async getCampanhaContatos(id: number, page = 1, limit = 50): Promise<{ contatos: DisparoEmailContato[]; total: number }> {
    const result = (await api.get(`/email/campanhas/${id}/contatos`, { params: { page: String(page), limit: String(limit) } })) as any;
    return { contatos: result?.data ?? [], total: result?.total ?? 0 };
  },

  async getCampanhaEntregaResumo(id: number): Promise<EntregaResumo> {
    const result = (await api.get(`/email/campanhas/${id}/entrega-resumo`)) as any;
    return result?.data ?? { total: 0, entregues: 0, abertos: 0, bounces: 0, complaints: 0 };
  },

  async iniciarCampanha(id: number): Promise<DisparoEmail> {
    const result = (await api.post(`/email/campanhas/${id}/iniciar`, {})) as any;
    return result?.data ?? result;
  },

  async cancelarCampanha(id: number): Promise<DisparoEmail> {
    const result = (await api.post(`/email/campanhas/${id}/cancelar`, {})) as any;
    return result?.data ?? result;
  },

  async pausarCampanha(id: number): Promise<DisparoEmail> {
    const result = (await api.post(`/email/campanhas/${id}/pausar`, {})) as any;
    return result?.data ?? result;
  },

  async retomarCampanha(id: number): Promise<DisparoEmail> {
    const result = (await api.post(`/email/campanhas/${id}/retomar`, {})) as any;
    return result?.data ?? result;
  },

  async reenviarFalhasCampanha(id: number): Promise<DisparoEmail> {
    const result = (await api.post(`/email/campanhas/${id}/reenviar-falhas`, {})) as any;
    return result?.data ?? result;
  },
};
